import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader } from "lucide-react";
import api, { formatPrice } from "../lib/api";
import { useCart } from "../contexts/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const redsysOrder = params.get("redsys_order");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const polling = useRef(false);

  useEffect(() => {
    if ((!sessionId && !redsysOrder) || polling.current) return;
    polling.current = true;

    let attempts = 0;
    const maxAttempts = 10;
    const orderId = params.get("internal_order") || localStorage.getItem("luxe-pending-order");
    const statusUrl = redsysOrder
      ? `/payments/redsys/status/${redsysOrder}`
      : `/payments/status/${sessionId}`;

    const check = async () => {
      try {
        const { data } = await api.get(statusUrl);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          localStorage.removeItem("luxe-pending-order");
          if (orderId) {
            const ord = await api.get(`/orders/${orderId}`);
            setOrder(ord.data);
          }
          return;
        }
        if (data.status === "expired" || attempts >= maxAttempts) {
          setStatus("expired");
          return;
        }
        attempts += 1;
        setTimeout(check, 2000);
      } catch {
        if (attempts >= maxAttempts) {
          setStatus("error");
        } else {
          attempts += 1;
          setTimeout(check, 2000);
        }
      }
    };
    check();
  }, [sessionId, redsysOrder, clear, params]);

  return (
    <div data-testid="checkout-success-page" className="pt-28 min-h-screen flex items-center">
      <div className="max-w-2xl mx-auto px-6 text-center py-20">
        {status === "checking" && (
          <>
            <Loader className="w-16 h-16 text-gold mx-auto mb-8 animate-spin" />
            <p className="overline mb-3">Procesando</p>
            <h1 className="font-heading text-4xl text-ivory mb-4">Verificando el pago...</h1>
            <p className="text-ivory/60">Por favor, no cierre esta ventana.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-8" />
            <p className="overline mb-3">Confirmado</p>
            <h1 className="font-heading text-5xl text-ivory mb-6">Gracias por su pedido</h1>
            <p className="text-ivory/70 mb-8 leading-relaxed">
              Su pedido ha sido confirmado. Recibirá un email con todos los detalles del envío y seguimiento.
            </p>
            {order && (
              <div className="bg-graphite border border-smoke p-6 mb-8 text-left">
                <div className="flex justify-between mb-3">
                  <span className="overline">Pedido</span>
                  <span className="text-gold font-heading">#{order.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ivory/60">Total</span>
                  <span className="text-ivory font-heading text-xl">{formatPrice(order.total)}</span>
                </div>
              </div>
            )}
            <div className="flex justify-center gap-3 flex-wrap">
              <Link to="/account" className="btn-gold" data-testid="view-orders-btn">Ver mis pedidos</Link>
              <Link to="/shop" className="btn-outline-gold">Seguir comprando</Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <XCircle className="w-16 h-16 text-rose mx-auto mb-8" />
            <h1 className="font-heading text-4xl text-ivory mb-4">No hemos podido confirmar el pago</h1>
            <p className="text-ivory/60 mb-8">Si el cargo se ha realizado, se procesará. De lo contrario, intente de nuevo.</p>
            <Link to="/checkout" className="btn-gold">Reintentar</Link>
          </>
        )}
      </div>
    </div>
  );
}
